import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/kit";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ITSupportDialog } from "@/components/ITSupportDialog";
import { LifeBuoy } from "lucide-react";

const STATUS = ["Open", "In Progress", "Resolved", "Closed"];
const STATUS_STYLE = {
  Open: "bg-amber-100 text-amber-800 border-amber-200",
  "In Progress": "bg-blue-100 text-blue-800 border-blue-200",
  Resolved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  Closed: "bg-slate-100 text-slate-600 border-slate-200",
};
const fmtWhen = (ts) => { try { return new Date(ts).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return ts; } };

export default function ITSupport() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAdmin = ["founder", "admin"].includes(user?.role);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["it-requests"],
    queryFn: async () => (await api.get("/it-requests")).data,
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }) => api.put(`/it-requests/${id}/status`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["it-requests"] }); toast.success("Status updated"); },
  });

  return (
    <div data-testid="it-support-page">
      <PageHeader title="IT Support" subtitle={isAdmin ? "All raised requests · resolve within 8 hours" : "Your IT & system requests"}>
        <ITSupportDialog />
      </PageHeader>

      <SectionCard title={isAdmin ? "All Requests" : "My Requests"} testId="it-requests-list">
        {isLoading ? (
          <Skeleton className="h-40 rounded-lg" />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-14 text-center" data-testid="it-empty">
            <div className="rounded-full bg-navy/5 p-4 mb-3"><LifeBuoy className="h-8 w-8 text-navy" /></div>
            <p className="text-sm font-medium text-slate-500">No requests yet</p>
            <p className="mt-1 text-xs text-slate-400">Tap “Contact IT Team” to raise your first request.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ref</TableHead>
                  <TableHead>When</TableHead>
                  {isAdmin && <TableHead>From</TableHead>}
                  <TableHead>Category</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.request_id} data-testid={`it-row-${r.request_id}`}>
                    <TableCell className="font-mono text-xs text-slate-500">{r.request_id}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-slate-600">{fmtWhen(r.created_at)}</TableCell>
                    {isAdmin && <TableCell className="text-sm"><div className="font-medium text-navy">{r.name}</div><div className="text-xs text-slate-400">{r.email}</div></TableCell>}
                    <TableCell className="text-sm text-slate-600">{r.category}</TableCell>
                    <TableCell className="max-w-xs"><div className="text-sm text-navy">{r.subject}</div>{r.description && <div className="truncate text-xs text-slate-400" title={r.description}>{r.description}</div>}</TableCell>
                    <TableCell>
                      {isAdmin ? (
                        <Select value={r.status} onValueChange={(v) => setStatus.mutate({ id: r.request_id, status: v })}>
                          <SelectTrigger className="w-36 h-8" data-testid={`it-status-${r.request_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      ) : (
                        <Badge variant="outline" className={STATUS_STYLE[r.status] || STATUS_STYLE.Open}>{r.status}</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
